import { useState, useCallback, useEffect, useRef } from 'react';
import { useUser } from '@/lib/context/user-context';

interface SavedWord {
  word: string;
  translation: string;
  phonetic?: string;
  example?: string;
  source: 'youdao' | 'original';
  explains?: string[];
  savedAt: number;
  addedAt?: number;
  status?: 'NEW' | 'FUZZY' | 'MASTERED' | string;
  lastReviewedAt?: number | null;
  nextReviewAt?: number | null;
  stageIndex?: number;
  ease?: number;
  interval?: number;
  reps?: number;
  correctStreak?: number;
  wrongStreak?: number;
  bootstrapDone?: boolean;
  episodeId?: string;
  episodeTitle?: string;
  episodeThumbnail?: string;
  contextText?: string;
}

interface SaveWordParams {
  word: string;
  translation: string;
  phonetic?: string;
  example?: string;
  source: 'youdao' | 'original';
  explains?: string[];
  episodeId?: string;
  episodeTitle?: string;
}

export function useSavedWords() {
  const { user } = useUser();
  const [savedWords, setSavedWords] = useState<Record<string, boolean>>({});
  const [savingWords, setSavingWords] = useState<Record<string, boolean>>({});
  const [allSavedWords, setAllSavedWords] = useState<SavedWord[]>([]);
  const [loading, setLoading] = useState(false);

  // 检查单词是否已保存
  const checkWordSaved = useCallback(async (word: string): Promise<boolean> => {
    if (!user?.id || !word) return false;

    // DISABLED: This was causing frequent API calls
    // Use the bulk fetch in fetchAllSavedWords instead
    console.warn('checkWordSaved is disabled to prevent frequent API calls. Use fetchAllSavedWords instead.');
    return false;
  }, [user?.id]);

  // 保存单词
  const saveWord = useCallback(async (params: SaveWordParams): Promise<boolean> => {
    if (!user?.id) {
      console.error('User not authenticated');
      return false;
    }

    const wordKey = params.word.toLowerCase();
    setSavingWords(prev => ({ ...prev, [wordKey]: true }));

    try {
      const response = await fetch('/api/user/saved-words', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          ...params,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save word');
      }

      // 更新本地状态
      setSavedWords(prev => ({ ...prev, [wordKey]: true }));

      // 触发全部单词列表刷新（如果已加载）
      if (allSavedWords.length > 0) {
        fetchAllSavedWords();
      }

      return true;
    } catch (error) {
      console.error('Error saving word:', error);
      return false;
    } finally {
      setSavingWords(prev => ({ ...prev, [wordKey]: false }));
    }
  }, [user?.id, allSavedWords.length]);

  // 删除单词
  const removeWord = useCallback(async (word: string): Promise<boolean> => {
    if (!user?.id) return false;

    const wordKey = word.toLowerCase();
    setSavingWords(prev => ({ ...prev, [wordKey]: true }));

    try {
      const response = await fetch(`/api/user/saved-words?userId=${user.id}&word=${encodeURIComponent(wordKey)}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to remove word');
      }

      // 更新本地状态
      setSavedWords(prev => ({ ...prev, [wordKey]: false }));

      // 从全部单词列表中移除
      setAllSavedWords(prev => prev.filter(w => w.word.toLowerCase() !== wordKey));

      return true;
    } catch (error) {
      console.error('Error removing word:', error);
      return false;
    } finally {
      setSavingWords(prev => ({ ...prev, [wordKey]: false }));
    }
  }, [user?.id]);

  const reviewWord = useCallback(async (word: string, quality: number, now?: number) => {
    if (!user?.id) return null;

    try {
      const response = await fetch('/api/user/saved-words', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId: user.id, word, quality, now }),
      });

      if (!response.ok) {
        throw new Error('Failed to review word');
      }

      const data = await response.json();
      if (data?.word) {
        setAllSavedWords(prev => {
          const index = prev.findIndex((item) => item.word === data.word.word);
          if (index === -1) return prev;
          const next = [...prev];
          next[index] = { ...next[index], ...data.word };
          return next;
        });
      }

      return data?.word ?? null;
    } catch (error) {
      console.error('Error reviewing word:', error);
      return null;
    }
  }, [user?.id]);

  // 获取所有保存的单词
  const fetchAllSavedWords = useCallback(async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const response = await fetch(`/api/user/saved-words?userId=${user.id}`, {
        cache: 'no-store',
      });
      const data = await response.json();

      if (data.savedWords) {
        setAllSavedWords(data.savedWords);

        // 更新单词保存状态映射
        const savedWordsMap: Record<string, boolean> = {};
        data.savedWords.forEach((word: SavedWord) => {
          savedWordsMap[word.word.toLowerCase()] = true;
        });
        setSavedWords(savedWordsMap);
      }
    } catch (error) {
      console.error('Error fetching saved words:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // 检查单词是否已保存（同步方法）
  const isWordSaved = useCallback((word: string): boolean => {
    return savedWords[word.toLowerCase()] || false;
  }, [savedWords]);

  // 检查单词是否正在保存
  const isWordSaving = useCallback((word: string): boolean => {
    return savingWords[word.toLowerCase()] || false;
  }, [savingWords]);

  const hasLoadedRef = useRef(false);
  useEffect(() => {
    if (!user?.id) {
      hasLoadedRef.current = false;
      setAllSavedWords([]);
      setSavedWords({});
      setLoading(false);
      return;
    }
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      void fetchAllSavedWords();
    }
  }, [user?.id, fetchAllSavedWords]);

  return {
    // 状态
    savedWords: allSavedWords,
    loading,

    // 方法
    saveWord,
    removeWord,
    fetchAllSavedWords,
    reviewWord,
    checkWordSaved,
    isWordSaved,
    isWordSaving,
  };
}
